import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { clearToken } from "../../api/client";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

const forceChangeSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().min(6, "New password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ForceChangeForm = z.infer<typeof forceChangeSchema>;

export function ForcePasswordChangePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve currentPassword from location.state if passed from LoginPage
  const passedCurrentPassword = (location.state as { currentPassword?: string })?.currentPassword || "";

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForceChangeForm>({
    resolver: zodResolver(forceChangeSchema),
    defaultValues: {
      currentPassword: passedCurrentPassword,
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ForceChangeForm) => {
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.changePassword({
        current_password: data.currentPassword,
        new_password: data.newPassword,
      });

      if (!result.success) {
        setError(result.message || "Failed to update password. Please check your credentials.");
        return;
      }

      // Successful change: clear the temporary token and show success message
      clearToken();
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToLogin = () => {
    navigate("/login", { replace: true });
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
        <Card className="w-full max-w-md" title={t("auth.passwordUpdatedTitle")}>
          <div className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              {t("auth.passwordUpdatedDesc")}
            </div>
            <Button onClick={handleReturnToLogin} className="w-full">
              {t("auth.proceedToSignInButton")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.updateYourPasswordTitle")}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-text-muted">
            {t("auth.updateYourPasswordDesc")}
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm animate-shake">
              {error}
            </div>
          )}

          <Input
            label={t("auth.currentPasswordLabel")}
            type="password"
            placeholder="••••••••"
            error={errors.currentPassword?.message}
            {...register("currentPassword")}
          />

          <Input
            label={t("auth.newPasswordLabel")}
            type="password"
            placeholder="••••••••"
            error={errors.newPassword?.message}
            {...register("newPassword")}
          />

          <Input
            label={t("auth.confirmNewPasswordLabel")}
            type="password"
            placeholder="••••••••"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />

          <Button type="submit" className="w-full" loading={loading}>
            {t("auth.changePasswordButton")}
          </Button>

          <p className="text-center text-sm text-text-muted">
            <button
              type="button"
              onClick={() => {
                clearToken();
                navigate("/login", { replace: true });
              }}
              className="text-brand hover:underline"
            >
              {t("auth.cancelAndGoBack")}
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
}
